import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { WelcomeConfetti } from '../../components/WelcomeConfetti';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Calendar, Users, FileText, Heart, Clock, BookOpen, Pill,
  ChevronRight, Smile, Meh, Frown, Send, AlertCircle, CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MOOD_ICONS = { happy: Smile, neutral: Meh, sad: Frown };
const MOOD_COLORS = { happy: 'text-emerald-400', neutral: 'text-amber-400', sad: 'text-blue-400' };

export function ClientDashboard() {
  const { user } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);
  const [journals, setJournals] = useState([]);
  const [medications, setMedications] = useState([]);
  const [carePlan, setCarePlan] = useState(null);
  const [wellnessGoals, setWellnessGoals] = useState([]);
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [alterations, setAlterations] = useState([]);
  const [alterDialog, setAlterDialog] = useState(false);
  const [alterForm, setAlterForm] = useState({ section: '', description: '', current_value: '', requested_value: '' });

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('client_welcome_seen');
    if (!hasSeenWelcome && user?.role === 'client') {
      setShowWelcome(true);
      localStorage.setItem('client_welcome_seen', 'true');
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [jRes, mRes, cpRes, wgRes, shRes, altRes] = await Promise.all([
        axios.get(`${API_URL}/api/care-journals`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/medications`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/care-plans`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/wellness-goals`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/shifts?status=assigned`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/care-plan-alterations`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setJournals(jRes.data?.slice?.(0, 5) || []);
      setMedications(mRes.data?.slice?.(0, 6) || []);
      setCarePlan(Array.isArray(cpRes.data) ? cpRes.data[0] : cpRes.data);
      setWellnessGoals(Array.isArray(wgRes.data) ? wgRes.data.slice(0, 4) : []);
      setUpcomingShifts(Array.isArray(shRes.data) ? shRes.data.slice(0, 5) : []);
      setAlterations(Array.isArray(altRes.data) ? altRes.data : []);
    } catch (err) { console.error(err); }
  };

  const handleRequestAlteration = async () => {
    if (!alterForm.description) return toast.error('Please describe the requested change');
    if (!carePlan?.id) return toast.error('No active care plan');
    try {
      await axios.post(`${API_URL}/api/care-plans/${carePlan.id}/request-alteration`, alterForm, { withCredentials: true });
      toast.success('Alteration request submitted for admin approval');
      setAlterDialog(false);
      setAlterForm({ section: '', description: '', current_value: '', requested_value: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <DashboardLayout>
      <WelcomeConfetti show={showWelcome} onComplete={() => setShowWelcome(false)} />

      <div className="space-y-8" data-testid="client-dashboard">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
            Welcome, {user?.name}!
          </h1>
          <p className="text-muted-foreground mt-1">Your care dashboard</p>
        </div>

        {/* Quick Stats */}
        <div className="grid sm:grid-cols-4 gap-4">
          <Card className="glass-card border-border">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-muted-foreground text-xs">Upcoming Visits</p>
                  <p className="text-2xl font-heading font-bold text-[#00A6A0] mt-1">{upcomingShifts.length}</p>
                </div>
                <Calendar className="w-5 h-5 text-[#00A6A0]" />
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card border-border">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-muted-foreground text-xs">Medications</p>
                  <p className="text-2xl font-heading font-bold text-[#A8E6CF] mt-1">{medications.length}</p>
                </div>
                <Pill className="w-5 h-5 text-[#A8E6CF]" />
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card border-border">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-muted-foreground text-xs">Wellness Goals</p>
                  <p className="text-2xl font-heading font-bold text-[#FF6F61] mt-1">{wellnessGoals.length}</p>
                </div>
                <Heart className="w-5 h-5 text-[#FF6F61]" />
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card border-border">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-muted-foreground text-xs">Care Plan</p>
                  <p className="text-2xl font-heading font-bold text-foreground mt-1">{carePlan ? 'Active' : '--'}</p>
                </div>
                <FileText className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Care Schedule */}
        <Card className="glass-card border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#00A6A0]" />
              Upcoming Care Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingShifts.length === 0 ? (
              <p className="text-muted-foreground text-center py-6">No upcoming scheduled visits</p>
            ) : (
              <div className="space-y-2">
                {upcomingShifts.map((s, idx) => (
                  <div key={s.id || idx} className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-[#00A6A0]/10"><Clock className="w-4 h-4 text-[#00A6A0]" /></div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{s.date} — {s.start_time} to {s.end_time}</p>
                        <p className="text-xs text-muted-foreground">{s.caregiver_name || 'Carer assigned'} {s.notes ? `· ${s.notes}` : ''}</p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${s.status === 'assigned' ? 'bg-[#00A6A0]/10 text-[#00A6A0]' : 'bg-amber-500/10 text-amber-400'}`}>{s.status}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Care Plan (Read-Only with Request Alteration) */}
        <Card className="glass-card border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#8B5CF6]" />
              Your Care Plan
            </CardTitle>
            {carePlan && (
              <Button size="sm" variant="outline" className="border-[#FF6F61]/50 text-[#FF6F61]" onClick={() => setAlterDialog(true)} data-testid="request-alteration-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" />Request Change
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {!carePlan ? (
              <p className="text-muted-foreground text-center py-6">No active care plan</p>
            ) : (
              <div className="space-y-4">
                {carePlan.care_needs?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Care Needs</p>
                    <div className="space-y-1.5">
                      {carePlan.care_needs.map((need, i) => (
                        <div key={i} className="p-2.5 bg-muted rounded-lg border border-border text-sm text-foreground">{typeof need === 'string' ? need : need.description || need.name || JSON.stringify(need)}</div>
                      ))}
                    </div>
                  </div>
                )}
                {carePlan.medications?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Medications</p>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {carePlan.medications.map((m, i) => (
                        <div key={i} className="p-2.5 bg-muted rounded-lg border border-border">
                          <p className="text-sm font-medium text-foreground">{typeof m === 'string' ? m : m.name || m.medication_name}</p>
                          {m.dosage && <p className="text-xs text-[#00A6A0]">{m.dosage}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {carePlan.notes && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Notes</p>
                    <p className="text-sm text-muted-foreground bg-muted p-3 rounded-lg border border-border">{carePlan.notes}</p>
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground">Created: {new Date(carePlan.created_at).toLocaleDateString()} {carePlan.created_by_name && `by ${carePlan.created_by_name}`}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alteration Requests */}
        {alterations.length > 0 && (
          <Card className="glass-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-400" />
                Your Change Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {alterations.map(a => (
                  <div key={a.id} className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                    <div>
                      <p className="text-sm text-foreground">{a.description}</p>
                      {a.section && <p className="text-xs text-muted-foreground">Section: {a.section}</p>}
                      <p className="text-[10px] text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'approved' ? 'bg-emerald-500/10 text-emerald-400' : a.status === 'rejected' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'}`}>
                      {a.status}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Care Journals */}
        <Card className="glass-card border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#00A6A0]" />
              Recent Care Journals
            </CardTitle>
            <Link to="/care-journals">
              <Button variant="ghost" size="sm" className="text-[#00A6A0]" data-testid="client-view-journals">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {journals.length === 0 ? (
              <p className="text-muted-foreground text-center py-6">No care journal entries yet</p>
            ) : (
              <div className="space-y-3">
                {journals.map(j => {
                  const MoodIcon = MOOD_ICONS[j.mood] || Meh;
                  return (
                    <div key={j.id} className="flex items-start gap-3 p-3 bg-muted rounded-lg border border-border">
                      <MoodIcon className={`w-5 h-5 mt-0.5 ${MOOD_COLORS[j.mood] || 'text-muted-foreground'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm text-foreground font-medium">{j.caregiver_name}</span>
                          <span className="text-[10px] text-muted-foreground">{new Date(j.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">{j.content}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Wellness Goals */}
        {wellnessGoals.length > 0 && (
          <Card className="glass-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#FF6F61]" />
                Wellness Goals
              </CardTitle>
              <Link to="/wellness-goals">
                <Button variant="ghost" size="sm" className="text-[#00A6A0]" data-testid="client-view-wellness">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {wellnessGoals.map(g => (
                  <div key={g.id} className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                    <div>
                      <p className="text-sm text-foreground">{g.title || g.goal}</p>
                      <p className="text-[10px] text-muted-foreground">{g.category || 'General'}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${g.status === 'achieved' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
                      {g.status || 'In Progress'}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Your Details */}
        <Card className="glass-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-foreground">Your Details</CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Your USIC</p>
              <p className="text-[#FF6F61] font-mono">{user?.usic}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="text-foreground">{user?.email}</p>
            </div>
          </CardContent>
        </Card>

        {/* Alteration Request Dialog */}
        <Dialog open={alterDialog} onOpenChange={setAlterDialog}>
          <DialogContent className="glass-card border-border sm:max-w-md">
            <DialogHeader><DialogTitle className="text-foreground">Request Care Plan Change</DialogTitle><DialogDescription className="text-muted-foreground">Your request will be reviewed by the agency admin</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-foreground">Section (optional)</Label>
                <Input value={alterForm.section} onChange={e => setAlterForm(p => ({...p, section: e.target.value}))} placeholder="e.g., Medications, Visit times, Care needs" className="mt-1 bg-muted border-border text-foreground" data-testid="alter-section" /></div>
              <div><Label className="text-foreground">What would you like changed? *</Label>
                <textarea value={alterForm.description} onChange={e => setAlterForm(p => ({...p, description: e.target.value}))} placeholder="Describe the change you'd like..." className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[80px] resize-none" data-testid="alter-description" /></div>
              <div><Label className="text-foreground">Current arrangement (optional)</Label>
                <Input value={alterForm.current_value} onChange={e => setAlterForm(p => ({...p, current_value: e.target.value}))} placeholder="What's currently in place" className="mt-1 bg-muted border-border text-foreground" /></div>
              <div><Label className="text-foreground">Your preference (optional)</Label>
                <Input value={alterForm.requested_value} onChange={e => setAlterForm(p => ({...p, requested_value: e.target.value}))} placeholder="What you'd prefer instead" className="mt-1 bg-muted border-border text-foreground" /></div>
              <Button className="w-full bg-[#FF6F61] hover:bg-[#FF6F61]/90 text-white" onClick={handleRequestAlteration} data-testid="submit-alteration-btn">
                <Send className="w-4 h-4 mr-1.5" />Submit Request
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

export default ClientDashboard;
